<template>
  <div class="class-1">
    <!-- Header -->
    <AppHeader title="Printers Management">
    </AppHeader>

    <!-- Search and Add New Printer Button -->
    <div class="search-add-container">
      <input 
        type="text" 
        v-model="searchQuery" 
        placeholder="Search printers..." 
        class="search-input"
      />
      <button class="add-printer-btn" @click="navigateToAddPrinter">Add New Printer</button>
    </div>

    <!-- Table Structure -->
    <div class="table-container">
      <table id="printerTable">
        <thead>
          <tr>
            <th>Name</th>
            <th>IP Address</th>
            <th>Languages</th>
            <th>Status</th>
            <th>Operations</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(printer) in paginatedPrinters" :key="printer.id">
            <td>{{ getPrinterName(printer) }}</td>
            <td>{{ printer.ip_address || 'Not Set' }}</td>
            <td>
              <span v-for="(lang, langIndex) in printer.languages" :key="lang.langId" class="language-tag">
                {{ getLanguageName(lang.langId) }}: {{ lang.name }}
                <span v-if="langIndex < printer.languages.length - 1">, </span>
              </span>
            </td>
            <td :class="printer.isSuspend ? 'text-danger' : 'text-success'">
              {{ printer.isSuspend ? 'Suspended' : 'Active' }}
            </td>
            <td class="action-buttons">
              <button data-tooltip="Edit" @click="handleEdit(printer)">
                <img src="@/assets/edit_icon.png" alt="Edit">
              </button>
              <button 
                data-tooltip="Delete" 
                @click="handleDelete(printer)"
                class="delete-btn"
              >
                <img src="@/assets/delete_icon.png" alt="Delete">
              </button>
              <button 
                :data-tooltip="printer.isSuspend ? 'Activate' : 'Suspend'" 
                @click="handleToggleStatus(printer)"
                :class="printer.isSuspend ? 'activate-btn' : 'suspend-btn'"
              >
                <img 
                  :src="printer.isSuspend 
                    ? require('@/assets/activate_icon.png') 
                    : require('@/assets/suspend_icon.png')" 
                  :alt="printer.isSuspend ? 'Activate' : 'Suspend'"
                >
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="paginatedPrinters.length === 0" class="no-data">
        No printers found
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-container" v-if="totalPages > 1">
      <button 
        @click="prevPage" 
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        Previous
      </button>
      
      <span class="page-info">
        Page {{ currentPage }} of {{ totalPages }}
      </span>
      
      <button 
        @click="nextPage" 
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        Next
      </button>
      
      <select v-model="itemsPerPage" @change="resetPagination" class="page-size-select">
        <option value="5">5 per page</option>
        <option value="10">10 per page</option>
        <option value="20">20 per page</option>
        <option value="40">40 per page</option>
      </select>
      <button class="back-btn" @click="goBack">
        <i class="fas fa-arrow-left"></i> Back
      </button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      printers: [],
      languages: [],
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      showBackButton: true
    };
  },
  computed: {
    filteredPrinters() {
      const query = this.searchQuery.toLowerCase();
      if (!query) return this.printers;
      
      return this.printers.filter(printer => {
        // Search in printer names across all languages
        const printerNames = printer.languages.map(lang => 
          lang.name.toLowerCase()
        ).join(' ');
        if (printerNames.includes(query)) return true;
        
        // Search in IP address
        if (printer.ip_address && printer.ip_address.toLowerCase().includes(query)) return true;
        
        // Search in language names
        const languageNames = printer.languages.map(lang => 
          this.getLanguageName(lang.langId).toLowerCase()
        ).join(' ');
        if (languageNames.includes(query)) return true;
        
        // Search in status
        const status = printer.isSuspend ? 'suspended' : 'active';
        if (status.includes(query)) return true;
        
        return false;
      });
    },
    paginatedPrinters() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPrinters.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredPrinters.length / this.itemsPerPage);
    }
  },
  mounted() {
    this.fetchPrinters();
    this.fetchLanguages();
    this.checkBackButton();
  },
  methods: {
    checkBackButton() {
      this.showBackButton = window.history.length > 1;
    },
    goBack() {
      this.$router.go(-1);
    },
    getPrinterName(printer) {
      if (printer.languages && printer.languages.length > 0) {
        // Try to find name in English (langId 0) first, then fallback to first available
        const englishName = printer.languages.find(lang => lang.langId === 0)?.name;
        return englishName || printer.languages[0]?.name || `Printer ${printer.id}`;
      }
      return `Printer ${printer.id}`;
    },
    getLanguageName(langId) {
      const language = this.languages.find(lang => lang.id === langId);
      return language ? language.fullName : `Language ${langId}`;
    },
    async fetchPrinters() {
      try {
        this.$root.isLoading = true; 
        const token = localStorage.getItem('authToken'); 
        const response = await fetch(`${this.$BASE_API_URL}/api/printers`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch printers');
        }

        const { data } = await response.json();
        this.printers = data;
      } catch (error) {
        console.error('Error fetching printers:', error);
        this.$toast.error('Failed to load printer data');
      } finally {
        this.$root.isLoading = false;
      }
    },
    async fetchLanguages() {
      const storedLanguages = localStorage.getItem('languages');
      if (storedLanguages) {
        this.languages = JSON.parse(storedLanguages);
      } else {
        // Fallback: fetch languages from API if not in localStorage
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${this.$BASE_API_URL}/api/languages`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const { data } = await response.json();
            this.languages = data;
          }
        } catch (error) {
          console.error('Error fetching languages:', error);
        }
      }
    },
    navigateToAddPrinter() {
      if (!this.$hasPermission('create printers')){
        this.$toast.error('Not Permitted');
        return;
      }
      this.$router.push({ name: "AddPrinterComponent" });
    },
    handleEdit(printer) {
      if (!this.$hasPermission('edit printers')){
        this.$toast.error('Not Permitted');
        return;
      }
      this.$router.push({ 
        name: "EditPrinterComponent", 
        params: { id: printer.id } 
      });
    },
    async handleDelete(printer) {
      if (!this.$hasPermission('delete printers')){
        this.$toast.error('Not Permitted');
        return;
      }
      if (!confirm(`Are you sure you want to delete ${this.getPrinterName(printer)}?`)) {
        return;
      }
      
      try {
        this.$root.isLoading = true; 
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${this.$BASE_API_URL}/api/printers/${printer.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete printer');
        }

        this.printers = this.printers.filter(p => p.id !== printer.id);
        this.$toast.success('Printer deleted successfully');
        this.resetPagination();
      } catch (error) {
        console.error('Error deleting printer:', error);
        this.$toast.error('Failed to delete printer');
      } finally {
        this.$root.isLoading = false;
      }
    },
    async handleToggleStatus(printer) {
      try {
        if (!this.$hasPermission('edit printers')){
          this.$toast.error('Not Permitted');
          return;
        }
        
        this.$root.isLoading = true; 
        const token = localStorage.getItem('authToken');
        const newStatus = !printer.isSuspend;  
        
        const response = await fetch(`${this.$BASE_API_URL}/api/printers/${printer.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                ip_address: printer.ip_address,
                isSuspend: newStatus,
                names: printer.languages.map(lang => ({
                  langId: lang.langId,
                  name: lang.name
                }))
            })
        });

        if (!response.ok) {
            throw new Error('Failed to update printer status');
        }

        printer.isSuspend = newStatus;
        this.$forceUpdate();
        this.$toast.success(`Printer ${newStatus ? 'suspended' : 'activated'} successfully`);
      } catch (error) {
          console.error('Error updating printer status:', error);
          this.$toast.error('Failed to update printer status');
      } finally {
        this.$root.isLoading = false;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    resetPagination() {
      this.currentPage = 1;
    }
  },
  watch: {
    searchQuery() {
      this.resetPagination();
    }
  }
};
</script>

<style scoped>
th, td {
  border: 1px solid;
  padding: 12px;
  text-align: left;
}

.search-add-container {
  display: flex;
  justify-content: space-between;
  margin: 20px;
  align-items: center;
}

.search-input {
  padding: 10px;
  width: 300px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  color: #000;
}

.add-printer-btn {
  padding: 10px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  font-size: 14px;
}

.add-printer-btn:hover {
  background-color: #45a049;
}

.back-btn {
  padding: 8px 12px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
}

.back-btn:hover {
  background-color: #5a6268;
}

.table-container {
  overflow-x: auto;
  margin: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 10px;
  padding: 20px;
}

.pagination-btn {
  padding: 8px 16px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.pagination-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.page-info {
  margin: 0 10px;
  color: var(--font-color);
}

.page-size-select {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin-left: 10px;
}

.text-success {
  color: #28a745;
}

.text-danger {
  color: #dc3545;
}

.delete-btn img {
  filter: invert(20%) sepia(100%) saturate(500%) hue-rotate(300deg);
}

.suspend-btn {
  color: #dc3545;
}

.activate-btn {
  color: #28a745;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-buttons button:hover {
  background-color: #f5f5f5;
}

.action-buttons img {
  width: 18px;
  height: 18px;
}

.language-tag {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  margin: 2px;
  font-size: 12px;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-style: italic;
}

/* Tooltip styles */
[data-tooltip] {
  position: relative;
}

[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
}
</style>